import {
  buildWorkspaceAdminPath,
  parseWorkspaceContextFromPathname,
} from "@/features/Workspaces/workspaceRoutes";
// Import of the module

import { formatNumber, parseDate } from "@/lib/utils";
import i18n from "@/i18n";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";

// Strategy status mapping
const getStrategyStatusLabel = (status: number | null | undefined): string => {
  if (status === null || status === undefined) return "";

  const statusMap: Record<number, string> = {
    0: "Draft",
    1: "Waiting Approval",
    2: "Listed",
    3: "Hidden",
    4: "Deprecated",
    5: "Onboarding",
    6: "Created by ETL",
    7: "Under Investigation",
    8: "Published",
  };

  return statusMap[status] || `Unknown (${status})`;
};

// Type of the fields in the table

export type StrategyVersion = {
  id: number;
  code?: string | null;
  weight?: number | null;
  market_value?: number | null;
  balance_sheet?: number | null;
  exposition?: number | null;
  strategy_id?: number | null;
  version_id?: number | null;
  derivative_to_id?: number | null;
  cap?: number | null;
  floor?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  strategy_isin?: string | null;
  strategy_name?: string | null;
  company_name?: string | null;
  company_id?: number | null;
  strategy_type?: string | null;
  strategy_live_date?: string | null;
  strategy_status?: number | null;
  strategy_created_at?: string | null;
};

const REFERENTIAL_WORKSPACE = "qtl - referential";

const buildReferentialAdminLink = (path: string): string => {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const context = parseWorkspaceContextFromPathname(pathname);

  return buildWorkspaceAdminPath({
    group: context?.group,
    workspace: REFERENTIAL_WORKSPACE,
    path: `qtl - referential/${path}`,
  });
};

// Definition of the columns

export const strategy_versions_columns: ColumnDef<StrategyVersion>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "code",
    header: "Code",
    cell: ({ row }) => {
      const value = row.getValue("code");
      return value || "";
    },
  },
  {
    accessorKey: "weight",
    header: () => (
      <div className="text-right">
        {i18n.t("strategy_versions.columns.weight", {
          ns: "tables",
          defaultValue: "Weight",
        })}
      </div>
    ),
    cell: ({ row }) => {
      const value = row.getValue("weight") as number | null | undefined;
      return (
        <div className="text-right">
          {value !== null && value !== undefined ? value.toFixed(1) : ""}
        </div>
      );
    },
  },
  {
    accessorKey: "market_value",
    header: () => (
      <div className="text-right">
        {i18n.t("strategy_versions.columns.market_value", {
          ns: "tables",
          defaultValue: "Market Value",
        })}
      </div>
    ),
    cell: ({ row }) => {
      const value = row.getValue("market_value") as number | null | undefined;
      return (
        <div className="text-right">
          {value !== null && value !== undefined ? formatNumber(value) : ""}
        </div>
      );
    },
  },
  {
    accessorKey: "balance_sheet",
    header: () => (
      <div className="text-right">
        {i18n.t("strategy_versions.columns.balance_sheet", {
          ns: "tables",
          defaultValue: "Balance Sheet",
        })}
      </div>
    ),
    cell: ({ row }) => {
      const value = row.getValue("balance_sheet") as number | null | undefined;
      return (
        <div className="text-right">
          {value !== null && value !== undefined ? formatNumber(value) : ""}
        </div>
      );
    },
  },
  {
    accessorKey: "exposition",
    header: () => (
      <div className="text-right">
        {i18n.t("strategy_versions.columns.exposition", {
          ns: "tables",
          defaultValue: "Exposition",
        })}
      </div>
    ),
    cell: ({ row }) => {
      const value = row.getValue("exposition") as number | null | undefined;
      return (
        <div className="text-right">
          {value !== null && value !== undefined ? formatNumber(value) : ""}
        </div>
      );
    },
  },
  {
    accessorKey: "strategy_id",
    header: "Strategy Id",
    cell: ({ row }) => {
      const value = row.getValue("strategy_id");
      return value !== null && value !== undefined ? value : "";
    },
  },
  {
    accessorKey: "derivative_to_id",
    header: "Derivative To Id",
    cell: ({ row }) => {
      const value = row.getValue("derivative_to_id");
      return value !== null && value !== undefined ? value : "";
    },
  },
  {
    accessorKey: "strategy_isin",
    header: "Strategy Isin",
    cell: ({ row }) => {
      const value = row.getValue("strategy_isin");
      return value || "";
    },
  },
  {
    accessorKey: "strategy_name",
    header: "Strategy Name",
    cell: ({ row }) => {
      const strategyName = row.getValue("strategy_name") as string;
      const strategyId = row.getValue("strategy_id") as number;
      if (strategyName && strategyId) {
        return (
          <Link
            href={buildReferentialAdminLink(`strategies/${strategyId}`)}
            className="text-xs"
            underline="hover"
          >
            {strategyName}
          </Link>
        );
      }
      return "";
    },
  },
  {
    accessorKey: "company_id",
    header: "Company ID",
    meta: { isHidden: true }, // Hidden column for data access
  },
  {
    accessorKey: "company_name",
    header: "Company Name",
    cell: ({ row }) => {
      const companyName = row.getValue("company_name") as string;
      const companyId = row.original.company_id as number;
      if (companyName) {
        return (
          <Link
            href={buildReferentialAdminLink(`companies/${companyId}`)}
            className="text-xs"
            underline="hover"
          >
            {companyName}
          </Link>
        );
      }
      return "";
    },
  },
  {
    accessorKey: "strategy_type",
    header: "Strategy Type",
    cell: ({ row }) => {
      const value = row.getValue("strategy_type");
      return value || "";
    },
  },
  {
    accessorKey: "strategy_live_date",
    header: "Strategy Live Date",
    cell: ({ row }) => {
      const value = row.getValue("strategy_live_date");
      return value ? parseDate(value as string) : "";
    },
  },

  {
    accessorKey: "strategy_status",
    header: "Strategy Status",
    cell: ({ row }) => {
      const value = row.getValue("strategy_status") as number;
      return getStrategyStatusLabel(value);
    },
  },
  {
    accessorKey: "strategy_created_at",
    header: "Strategy Created At",
    cell: ({ row }) => {
      const value = row.getValue("strategy_created_at");
      return value ? parseDate(value as string) : "";
    },
  },

  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
